import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, type EditorState, $createTextNode, $createParagraphNode } from "lexical";
import { useEffect, useState } from "react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

function OnChangeTextPlugin({ onChange }: { onChange: (text: string) => void }) {
  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const text = root.getTextContent();
      onChange(text);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

function SetInitialValuePlugin({ value }: { value: string }) {
  const [editor] = useLexicalComposerContext();
  const [hasSetInitialValue, setHasSetInitialValue] = useState(false);

  useEffect(() => {
    // Only set initial value once
    if (value && !hasSetInitialValue) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        const textNode = $createTextNode(value);
        paragraph.append(textNode);
        root.append(paragraph);
      });
      setHasSetInitialValue(true);
    }
  }, [editor, value, hasSetInitialValue]);

  return null;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = "Enter text...",
  className = "",
}: RichTextEditorProps) {
  const initialConfig = {
    namespace: "RichTextEditor",
    theme: {
      paragraph: "mb-1",
      text: {
        bold: "font-bold",
        italic: "italic",
        underline: "underline",
      },
    },
    onError: (error: Error) => {
      console.error(error);
    },
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`relative rounded-md border border-input ${className}`}>
        <PlainTextPlugin
          contentEditable={
            <ContentEditable className="min-h-[120px] max-h-[300px] overflow-y-auto outline-none py-2 px-3 text-sm" />
          }
          placeholder={
            <div className="absolute top-2 left-3 text-sm text-muted-foreground pointer-events-none">
              {placeholder}
            </div>
          }
          ErrorBoundary={() => <div>Error loading editor</div>}
        />
        <HistoryPlugin />
        <OnChangeTextPlugin onChange={onChange} />
        <SetInitialValuePlugin value={value} />
      </div>
    </LexicalComposer>
  );
}
